import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { ArcBox, ArcButton, ArcCheckbox, ArcStack, ArcText } from "@src/core/ui";
import { routesConfig } from "@src/core/routes";
import { ACCESS_TOKEN_KEY } from "@src/core/config/keys";
import { useAcceptTermsMutation, useTermsQuery } from "@src/features/auth/redux/api";

const SCROLLBAR_WIDTH = 4;


const AgreementPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [confirmed, setConfirmed] = useState(false);
  const [textExpanded, setTextExpanded] = useState(false);
  const [privacyExpanded, setPrivacyExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumbStyle, setThumbStyle] = useState({ height: 0, top: 0 });
  const [isScrollable, setIsScrollable] = useState(false);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const dragRef = useRef({ active: false, startY: 0, startScrollTop: 0 });
  const { data: terms } = useTermsQuery();
  const [acceptTerms, { isLoading }] = useAcceptTermsMutation();

  const termsText = terms?.termsAndConditions ?? '';
  const privacyText = terms?.privacyPolicy ?? '';

  const updateThumb = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const scrollable = scrollHeight - clientHeight;
    if (scrollable <= 0) {
      setIsScrollable(false);
      setThumbStyle({ height: 0, top: 0 });
      return;
    }
    setIsScrollable(true);
    const thumbHeight = Math.max(66, (clientHeight / scrollHeight) * clientHeight);
    const thumbTop = (scrollTop / scrollable) * (clientHeight - thumbHeight);
    setThumbStyle({ height: thumbHeight, top: thumbTop });
  }, []);

  const setScrollRefCallback = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node;
    setScrollEl(node);
  }, []);

  useEffect(() => {
    const el = scrollEl;
    if (!el) return;
    updateThumb();
    const ro = new ResizeObserver(updateThumb);
    ro.observe(el);
    const onScroll = () => updateThumb();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", onScroll);
    };
  }, [scrollEl, updateThumb]);

  const onTrackClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const rect = track.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const scrollable = el.scrollHeight - el.clientHeight;
    if (scrollable <= 0) return;
    el.scrollTop = (y / rect.height) * scrollable;
  }, []);

  const onThumbDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = {
      active: true,
      startY: e.clientY,
      startScrollTop: scrollRef.current?.scrollTop ?? 0,
    };
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current.active || !scrollRef.current || !trackRef.current) return;
      const delta = e.clientY - dragRef.current.startY;
      const trackHeight = trackRef.current.getBoundingClientRect().height;
      const scrollable = scrollRef.current.scrollHeight - scrollRef.current.clientHeight;
      scrollRef.current.scrollTop = dragRef.current.startScrollTop + (delta / trackHeight) * scrollable;
      dragRef.current.startY = e.clientY;
      dragRef.current.startScrollTop = scrollRef.current.scrollTop;
    };
    const onUp = () => {
      dragRef.current.active = false;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) {
      navigate(routesConfig.signIn);
    }
  }, [navigate]);

  const handleAccept = async () => {
    try {
      await acceptTerms().unwrap();
      navigate(routesConfig.app);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <ArcStack p={8}>
      <style>{`.agreement-scroll-area::-webkit-scrollbar { display: none; }`}</style>
      <ArcStack p={5} bgcolor="background.surface" borderRadius={'4px'}>
        <ArcStack spacing={0.1}>
          <ArcText.h3 mb={2} color='black' fontWeight={700}>
       Legal Updates
          </ArcText.h3>
          <ArcText fontSize={16} fontWeight={400}>
We’ve updated some of our legal documents. Please review and accept them to continue using the platform.          </ArcText>
        </ArcStack>
        <ArcBox bgcolor="white" sx={{ display: "flex", height: "55vh", mb: 3, mt: 3 }}>
          <ArcBox
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              border: "1px solid #979797",
              borderRadius: "4px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              bgcolor: "white",
            }}
          >
            <div
              ref={setScrollRefCallback}
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                padding: 20,
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
              className="agreement-scroll-area"
            >
              <ArcText fontWeight={700} fontSize={16}>1. Terms and Conditions</ArcText>
              <ArcText
                component="div"
                fontSize={16}
                fontWeight={400}
                sx={{
                  whiteSpace: "pre-wrap",
                  ...(textExpanded ? {} : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", textOverflow: "clip" }),
                }}
              >
                {termsText}
              </ArcText>
              {!textExpanded && (
                <ArcBox component="div" role="button" tabIndex={0} onClick={() => setTextExpanded(true)} onKeyDown={(e) => e.key === "Enter" && setTextExpanded(true)} sx={{ cursor: "pointer", mt: 0.5 }}>
                  <ArcText fontSize={16} color="brand.main" sx={{ textDecoration: "underline" }}>
                    More...
                  </ArcText>
                </ArcBox>
              )}
              <ArcText fontWeight={700} fontSize={16} marginTop={3}>2. Privacy Policy</ArcText>
              <ArcText
                component="div"
                fontSize={16}
                fontWeight={400}
                sx={{
                  whiteSpace: "pre-wrap",
                  ...(privacyExpanded ? {} : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", textOverflow: "clip" }),
                }}
              >
                {privacyText}
              </ArcText>
              {!privacyExpanded && (
                <ArcBox component="div" role="button" tabIndex={0} onClick={() => setPrivacyExpanded(true)} onKeyDown={(e) => e.key === "Enter" && setPrivacyExpanded(true)} sx={{ cursor: "pointer", mt: 0.5 }}>
                  <ArcText fontSize={16} color="brand.main" sx={{ textDecoration: "underline" }}>
                    More...
                  </ArcText>
                </ArcBox>
              )}
            </div>
          </ArcBox>
          {isScrollable && (
            <ArcBox
              ref={trackRef}
              onClick={onTrackClick}
              sx={{
                width: SCROLLBAR_WIDTH,
                flexShrink: 0,
                alignSelf: "stretch",
                position: "relative",
                cursor: "pointer",
                bgcolor: "#00000014",
                borderRadius: "100px",
              }}
            >
              <ArcBox
                component="div"
                onMouseDown={onThumbDown}
                sx={{
                  position: "absolute",
                  left: 0,
                  width: SCROLLBAR_WIDTH,
                  height: thumbStyle.height,
                  top: thumbStyle.top,
                  borderRadius: "100px",
                  bgcolor: theme.palette.brand.main,
                  cursor: "grab",
                  minHeight: 66,
                  "&:active": { cursor: "grabbing" },
                }}
              />
            </ArcBox>
          )}
        </ArcBox>

        <ArcStack mb={2}>
          <ArcText fontSize={16} color="black" fontWeight={400}>
            A copy of these legal documents is available at https://test-controlroom.asend.ai/app/profile
          </ArcText>
        </ArcStack>
        <ArcStack direction="row" justifyContent="space-between">
          <ArcStack direction="row" alignItems="center" width="60%">
            <ArcCheckbox
              size="small"
              color="brand"
              checked={confirmed}
              sx={{ marginLeft: '-10px'}}
              onChange={(_, checked) => setConfirmed(checked)}
            />
            <ArcText fontSize={16} color="black" fontWeight={400} sx={{ cursor: "pointer" }} onClick={() => setConfirmed((c) => !c)}>
            I have read and agree to the updated legal documents
            </ArcText>
          </ArcStack>
          <ArcStack direction="row" spacing={2}>
            <ArcButton.tertiary
              sx={{
                fontSize: 16,
                textTransform: "none !important",
                "& .MuiTypography-root": {
                  fontSize: 16,
                  textTransform: "none !important",
                },
              }}
            >
              Cancel
            </ArcButton.tertiary>
            <ArcButton.brand
              onClick={handleAccept}
              disabled={!confirmed || isLoading}
              sx={{
                width: "147px",
                fontSize: 16,
                textTransform: "none !important",
                backgroundColor: "#9E9EF3 !important",
                color: "white !important",
                "& .MuiTypography-root": {
                  fontSize: 16,
                  color: "white !important",
                  textTransform: "none !important",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#E7E7FB !important",
                  color: "white !important",
                  opacity: 1,
                },
                "&.Mui-disabled .MuiTypography-root": { color: "white !important" },
              }}
            >
              Accept
            </ArcButton.brand>
          </ArcStack>
        </ArcStack>
      </ArcStack>
    </ArcStack>
  );
};

export default AgreementPage;
